import { Minimize2, X } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef } from 'react';

import { Tip } from '@/components/ui/overlays';

import { DockLayer } from './components/DockLayer';
import { FloatingWindow } from './components/FloatingWindow';
import { PanelBody } from './components/PanelBody';
import { SnapPreview, TabDockHighlight } from './components/SnapPreview';
import { setTabStripEl, setWorkspaceEl, workspace } from './controller';
import { splitSide } from './snap';
import { useWorkspaceStore } from './store';
import { useShell } from '@/stores/shell';
import { isFloatingLike } from './types';

import './workspace.css';

/** A panel expanded to fill the whole workspace. */
function MaximizedWindow({ panelId }: { panelId: string }) {
  const p = useWorkspaceStore((s) => s.panels[panelId]);
  if (!p) return null;
  return (
    <div className="wp-card wp-max is-active" data-panel-id={panelId}>
      <div className="wp-winbar">
        <span className="wp-winbar__title">{p.title}</span>
        <span className="wp-winbar__spacer" />
        <Tip label="Restore">
          <button className="wp-winbtn" aria-label="Restore" onClick={() => workspace.restorePanel(panelId)}>
            <Minimize2 size={12} />
          </button>
        </Tip>
        <Tip label="Close">
          <button className="wp-winbtn" aria-label="Close" onClick={() => workspace.closePanel(panelId)}>
            <X size={13} />
          </button>
        </Tip>
      </div>
      <div className="wp-window__content">
        <PanelBody panelId={panelId} />
      </div>
    </div>
  );
}

/**
 * The workspace surface: THE whole app's center — a borderless Dockview tab
 * strip plus a floating/snapping window layer. Always mounted (App.tsx never
 * tears it down). The file explorer lives once, in the AppShell LeftBar's
 * Files section (`content/ExplorerSidebar`) — opening a file from there adds
 * a tab here; dragging a tab out floats it; dragging to an edge/corner snaps
 * it.
 */
export function WorkspaceSurface({ visible = true }: { visible?: boolean }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const tabStripRef = useRef<HTMLDivElement>(null);
  const panels = useWorkspaceStore((s) => s.panels);
  const maximizedId = useWorkspaceStore((s) => s.maximizedId);
  // Fullscreen ("borderless paper"): the tab strip is chrome too — hide it so
  // only the opened content remains (workspace.css `.wp-root--fullscreen`).
  const fullscreen = useShell((s) => s.fullscreen);

  const floatingIds = useMemo(
    () =>
      Object.values(panels)
        .filter((p) => isFloatingLike(p.mode) && p.id !== maximizedId)
        .map((p) => p.id),
    [panels, maximizedId],
  );

  // A panel snapped to a side turns that side into its own region whose header
  // sits at the very top; the main dock then shrinks to the COMPLEMENTARY half
  // (workspace.css `.wp-main[data-split]`) so the two tab rows sit side by side
  // — a real 50/50 split — instead of the snapped card floating over a
  // full-width dock. `splitSide` (pure, unit-tested) decides which side wins.
  const split = useMemo(() => splitSide(Object.values(panels).map((p) => p.mode)), [panels]);

  useLayoutEffect(() => {
    const el = stageRef.current;
    setWorkspaceEl(el);
    setTabStripEl(tabStripRef.current);
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return; // hidden tab — ignore
      useWorkspaceStore.setState({ workspaceRect: { x: 0, y: 0, width: r.width, height: r.height } });
      workspace.reflow();
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setWorkspaceEl(null);
      setTabStripEl(null);
    };
  }, []);

  return (
    <section
      className={`wp-root wp-root--content${visible ? '' : ' wp-root--hidden'}${fullscreen ? ' wp-root--fullscreen' : ''}`}
      aria-label="Workspace"
    >
      <div className="wp-main" data-split={split}>
        {/* The slot, not the dock, is what a split narrows — see `.wp-dock-slot`
            in workspace.css for why (Dockview owns an inline width on its root). */}
        <div className="wp-dock-slot">
          <DockLayer />
        </div>
        {/* Explicit tab-dock geometry: always present (docking works with 0 tabs),
            and only THIS strip qualifies as a dock target — never a document body. */}
        <div className="wp-tabstrip-ref" data-tabdock aria-hidden="true" ref={tabStripRef} />
        <TabDockHighlight />
        <div className="wp-stage" ref={stageRef}>
          {floatingIds.map((id) => (
            <FloatingWindow key={id} panelId={id} />
          ))}
          <SnapPreview />
        </div>
      </div>
      {maximizedId && <MaximizedWindow panelId={maximizedId} />}
    </section>
  );
}
